import {useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {PODReviewStatus} from '../../../../enums/DeliveryEnum';
import {
    GET_DELIVERY_POD_ABNORMAL_LIST,
    GetDeliveryPodAbnormalListResponse,
} from '../graphql/queries/getDeliveryPodAbnormalList';
import {PodAbnormalTaskOrder} from '../graphql/types/PodAbnormalType';
import {getRemainingMs, isPodUrgent} from '../utils/podAbnormalDisplay';

/**
 * 排序口径与移动端 DriverViolationReviewPage 完全一致：
 *  1. 已人工审核通过（podReviewStatus=4）统一沉底；
 *  2. 未审核组内，剩余 <1h 的临近截止项置顶；
 *  3. 同级按剩余时间升序。
 * 剩余时间与紧急判定复用 podAbnormalDisplay 的 getRemainingMs / isPodUrgent（单一口径）。
 */
export const sortPodAbnormalOrders = (orders: PodAbnormalTaskOrder[]): PodAbnormalTaskOrder[] => {
    return [...orders].sort((a, b) => {
        const remainingMsA = getRemainingMs(a);
        const remainingMsB = getRemainingMs(b);

        const isApprovedA = a.orderWithAddress.order.podReviewStatus === PODReviewStatus.MANUAL_APPROVED;
        const isApprovedB = b.orderWithAddress.order.podReviewStatus === PODReviewStatus.MANUAL_APPROVED;

        // 1. 已审核 vs 未审核 → 已审核放后面
        if (isApprovedA && !isApprovedB) return 1;
        if (!isApprovedA && isApprovedB) return -1;

        // 2. 都是未审核 → 紧急置顶 + 剩余时间升序
        if (!isApprovedA && !isApprovedB) {
            const isUrgentA = isPodUrgent(a);
            const isUrgentB = isPodUrgent(b);
            if (isUrgentA && !isUrgentB) return -1;
            if (!isUrgentA && isUrgentB) return 1;
            return remainingMsA - remainingMsB;
        }

        // 3. 都是已审核 → 按剩余时间升序
        return remainingMsA - remainingMsB;
    });
};

export interface UsePodAbnormalListResult {
    data: PodAbnormalTaskOrder[];
    loading: boolean;
    refetch: () => void;
}

/**
 * 查询当前用户（DSP 经理）的 POD 异常订单列表并前端排序。
 * 无入参——dspId 由后端从 token 解析（与移动端同源），故无越权风险。
 */
export function usePodAbnormalList(): UsePodAbnormalListResult {
    const {data, loading, refetch} = useQuery<GetDeliveryPodAbnormalListResponse>(
        GET_DELIVERY_POD_ABNORMAL_LIST,
        {
            // cache-and-network：进页先出缓存再后台拉最新；errorPolicy:all 出错时保留已有数据不清空列表
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all',
        }
    );

    const sorted = useMemo(() => {
        const list = data?.getDeliveryPODAbnormalTypeListForCurrentUser ?? [];
        return sortPodAbnormalOrders(list);
    }, [data]);

    // 仅首屏（尚无数据）算 loading；refetch 刷新时已有数据，不再让页面整体转圈/闪骨架，
    // 避免 cache-and-network 下每次刷新 loading 翻转造成的负优化。
    const firstLoad = loading && !data?.getDeliveryPODAbnormalTypeListForCurrentUser;

    return {data: sorted, loading: firstLoad, refetch: () => void refetch()};
}
